import React, { useMemo, useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, BookOpen, GraduationCap, Users, CheckCircle2, XCircle, Award, TrendingUp } from 'lucide-react';
import { StatCard } from '../../../shared/components/cards/StatCard';
import type { SubjectResult } from '../types';
import SubjectPerformanceChart from '../components/SubjectPerformanceChart';
import ResultYearDropdown from '../components/ResultYearDropdown';
import SchoolWiseTable from '../components/SchoolWiseTable';
import {
  useGetResultRoSummaryQuery,
  useGetResultRoSubjectStreamSummaryQuery,
  useGetResultRegionDetailsQuery,
} from '../services/api';
import type { ResultClassificationSchoolRecord } from '../services/api';
import type { ViewMode, MetricMode, ClassGroup } from '../components/classification/types';
import { BASE_GROUPS, STREAM_GROUP, METRIC_MODES, STREAM_SEGMENT_BY_DB_VALUE } from '../components/classification/constants';
import Chip from '../components/classification/Chip';
import ViewModeToggle from '../components/classification/ViewModeToggle';
import SectionLabel from '../components/classification/SectionLabel';
import EntityBarChart from '../components/classification/EntityBarChart';
import type { EntityRow } from '../components/classification/types';

const tabs = [
  { id: 'class10', label: 'Class 10', icon: BookOpen },
  { id: 'class12', label: 'Class 12', icon: GraduationCap },
] as const;
type TabKey = typeof tabs[number]['id'];

const mapSchoolsToEntityRows = (schools: ResultClassificationSchoolRecord[] = []): EntityRow[] =>
  schools
    .map((s) => ({
      name: s.schoolName,
      passPercentage: s.passPercentage,
      performanceIndex: s.performanceIndex,
      totalRegistered: s.totalRegistered,
      totalAppeared: s.totalAppeared,
      totalPassed: s.totalPassed,
      totalCompartment: s.totalCompartment,
      totalAbsent: s.totalAbsent,
      kvCode: String(s.kvCode),
      state: s.state,
      district: s.district,
    }))
    .filter((r) => r.totalAppeared > 0);

export default function RegionDrillDown() {
  const { regionCode } = useParams<{ regionCode: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialTab = (searchParams.get('class') as TabKey) || 'class10';
  const selectedYearParam = Number(searchParams.get('year') || '2025');
  const selectedYear = Number.isFinite(selectedYearParam) ? selectedYearParam : 2025;
  const [activeTab, setActiveTab] = useState<TabKey>(initialTab);

  // Filter state
  const [viewMode, setViewMode] = useState<ViewMode>('graphical');
  const [selectedGroup, setSelectedGroup] = useState<ClassGroup>('overall');
  const [selectedSegment, setSelectedSegment] = useState<string>('');
  const [metricMode, setMetricMode] = useState<MetricMode>('overall');

  const grade = activeTab === 'class10' ? 10 : 12;
  const decodedRegion = decodeURIComponent(regionCode || '').trim();

  // Stream summary is fetched here (rather than with the other calls below)
  // because the Stream segment buttons are built from what it reports.
  const { data: roSubjectStreamData } =
    useGetResultRoSubjectStreamSummaryQuery(decodedRegion ? { grade, year: selectedYear, roName: decodedRegion } : skipToken);
  const streamRows = roSubjectStreamData?.streams || [];

  /**
   * Stream segments actually present for this region and session. Vocational
   * runs in only a few regions, so offering it everywhere would give a segment
   * that returns nothing. Until the summary loads, fall back to the three
   * universal streams rather than flashing a Vocational button that may vanish.
   */
  const streamGroup = useMemo(() => {
    if (!streamRows.length) {
      return { ...STREAM_GROUP, segments: STREAM_GROUP.segments.filter((s) => s.value !== 'vocational') };
    }
    const present = new Set(
      streamRows
        .map((r) => STREAM_SEGMENT_BY_DB_VALUE[String(r.stream ?? r.streamLabel ?? '').trim().toUpperCase()])
        .filter(Boolean),
    );
    return { ...STREAM_GROUP, segments: STREAM_GROUP.segments.filter((s) => present.has(s.value)) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(streamRows.map((r) => r.stream ?? r.streamLabel))]);

  const allGroups = useMemo(
    () => (grade === 12 && streamGroup.segments.length ? [...BASE_GROUPS, streamGroup] : BASE_GROUPS),
    [grade, streamGroup],
  );
  const activeGroupDef = allGroups.find((g) => g.id === selectedGroup) ?? allGroups[0];
  const metric: 'passPercentage' | 'performanceIndex' = metricMode === 'pi' ? 'performanceIndex' : 'passPercentage';

  function handleGroupChange(g: ClassGroup) {
    const grp = allGroups.find((gr) => gr.id === g);
    setSelectedGroup(g);
    setSelectedSegment(grp && grp.segments.length > 0 ? grp.segments[0].value : '');
  }

  // Guard stream to class 12 only
  React.useEffect(() => {
    if (grade === 10 && selectedGroup === 'stream') handleGroupChange('overall');
  }, [grade]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleYearChange = (year: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('year', String(year));
    navigate({ search: `?${params.toString()}` }, { replace: true });
  };

  const handleTabChange = (tab: TabKey) => {
    setActiveTab(tab);
    const params = new URLSearchParams(searchParams);
    params.set('class', tab);
    navigate({ search: `?${params.toString()}` }, { replace: true });
  };

  // ── API calls ────────────────────────────────────────────────────────────
  const { data: roSummaryData, isFetching: isRoSummaryLoading, isError: isRoSummaryError } =
    useGetResultRoSummaryQuery(decodedRegion ? { grade, year: selectedYear, roName: decodedRegion } : skipToken);

  // Compute per-subcategory query args directly (no useMemo — RTK Query handles caching)
  // Group ids are already the `category` values /result/regiondetails expects
  // (gender, socialcategory, location, cwsn, pmshri, sponsoredby, stream), so they
  // pass through unchanged — the same way ClassificationAnalysis does it. An earlier
  // translation table here spoke the /result/rocategorysummary dialect instead
  // ('category', 'urbanrural'), which that endpoint rejects with a 400.
  const apiCat = selectedGroup !== 'overall' ? selectedGroup : null;
  const canQuery = !!decodedRegion && !!apiCat && !(selectedGroup === 'stream' && grade !== 12);
  const catSubs = activeGroupDef.segments;

  const q0 = canQuery && catSubs[0] ? { grade: grade as 10 | 12, year: selectedYear, roName: decodedRegion, category: apiCat!, subcategory: catSubs[0].value } : skipToken;
  const q1 = canQuery && catSubs[1] ? { grade: grade as 10 | 12, year: selectedYear, roName: decodedRegion, category: apiCat!, subcategory: catSubs[1].value } : skipToken;
  const q2 = canQuery && catSubs[2] ? { grade: grade as 10 | 12, year: selectedYear, roName: decodedRegion, category: apiCat!, subcategory: catSubs[2].value } : skipToken;
  const q3 = canQuery && catSubs[3] ? { grade: grade as 10 | 12, year: selectedYear, roName: decodedRegion, category: apiCat!, subcategory: catSubs[3].value } : skipToken;
  const q4 = canQuery && catSubs[4] ? { grade: grade as 10 | 12, year: selectedYear, roName: decodedRegion, category: apiCat!, subcategory: catSubs[4].value } : skipToken;

  const { data: catData0 } = useGetResultRegionDetailsQuery(q0);
  const { data: catData1 } = useGetResultRegionDetailsQuery(q1);
  const { data: catData2 } = useGetResultRegionDetailsQuery(q2);
  const { data: catData3 } = useGetResultRegionDetailsQuery(q3);
  const { data: catData4 } = useGetResultRegionDetailsQuery(q4);

  // ── Derived data ─────────────────────────────────────────────────────────
  const regionSummary = roSummaryData?.summary || null;
  const regionSchools = roSummaryData?.Schools || [];

  const regionSubjects: SubjectResult[] = useMemo(() => {
    if (grade === 10) {
      return (roSummaryData?.subjectwiseSummary || [])
        .filter((s) => s.subjectName && s.subjectName.toLowerCase() !== 'nan')
        .map((s) => ({
          subject: s.subjectName,
          totalAppeared: s.appeared,
          performanceIndex: s.performanceIndex,
          passPercentage: s.passPercentage,
          grades: { A1: s.gradeBands?.A1 ?? 0, A2: s.gradeBands?.A2 ?? 0, B1: s.gradeBands?.B1 ?? 0, B2: s.gradeBands?.B2 ?? 0, C1: s.gradeBands?.C1 ?? 0, C2: s.gradeBands?.C2 ?? 0, D1: s.gradeBands?.D1 ?? 0, D2: s.gradeBands?.D2 ?? 0, E: s.gradeBands?.E ?? 0 },
        }))
        .sort((a, b) => b.totalAppeared - a.totalAppeared);
    }
    return streamRows
      .filter((row) => { const l = (row.streamLabel || row.stream || '').trim(); return l !== '' && l.toLowerCase() !== 'nan'; })
      .map((row) => ({ subject: row.streamLabel || row.stream || 'Unknown', totalAppeared: row.appearedStudents, performanceIndex: row.performanceIndex, passPercentage: row.passPercentage, grades: { A1: 0, A2: 0, B1: 0, B2: 0, C1: 0, C2: 0, D1: 0, D2: 0, E: 0 } }))
      .sort((a, b) => b.totalAppeared - a.totalAppeared);
  }, [grade, roSummaryData, streamRows]);

  // School rows mapped to EntityRow for the bar chart
  const schoolEntityRows = useMemo<EntityRow[]>(() =>
    [...regionSchools]
      .map((s) => ({
        name: s.schoolName,
        passPercentage: s.passPercentage,
        performanceIndex: s.performanceIndex,
        totalRegistered: s.totalRegistered,
        totalAppeared: s.totalAppeared,
        totalPassed: s.totalPassed,
        totalCompartment: s.totalCompartment,
        totalAbsent: s.totalAbsent,
        kvCode: String(s.kvCode),
      }))
      .sort((a, b) => b[metric] - a[metric]),
  [regionSchools, metric]);

  // Category rows: combine one RO item per subcategory call, labelled by segment label
  const comparisonRows = useMemo<EntityRow[]>(() => {
    if (!canQuery) return [];
    const allData = [catData0, catData1, catData2, catData3, catData4];
    const rows: EntityRow[] = [];
    for (let i = 0; i < catSubs.length; i++) {
      const data = allData[i];
      if (!data?.RO?.length) continue;
      const roItem = data.RO.find((r) => r.roName.toUpperCase() === decodedRegion.toUpperCase()) ?? data.RO[0];
      if (!roItem || roItem.totalAppeared === 0) continue;
      rows.push({
        name: catSubs[i].label,
        passPercentage: roItem.passPercentage,
        performanceIndex: roItem.performanceIndex,
        totalRegistered: roItem.totalRegistered,
        totalAppeared: roItem.totalAppeared,
        totalPassed: roItem.totalPassed,
        totalCompartment: roItem.totalCompartment,
        totalAbsent: roItem.totalAbsent,
      });
    }
    // Legacy: Schools format fallback
    if (rows.length === 0 && catData0?.Schools) {
      return mapSchoolsToEntityRows(catData0.Schools);
    }
    return rows;
  }, [catData0, catData1, catData2, catData3, catData4, canQuery, catSubs, decodedRegion]);

  const displaySegmentLabel = useMemo(() => {
    if (selectedGroup === 'overall') return 'Overall';
    return activeGroupDef.label;
  }, [selectedGroup, activeGroupDef]);

  /**
   * Stats for the cards. On 'Overall' this is the whole region; once a
   * classification segment is picked the cards show that segment's own numbers
   * instead of silently continuing to show the region total.
   */
  const activeSegment = useMemo(() => {
    if (selectedGroup === 'overall' || !canQuery) return null;
    const idx = catSubs.findIndex((s) => s.value === selectedSegment);
    if (idx < 0) return null;
    const data = [catData0, catData1, catData2, catData3, catData4][idx];
    const ro =
      data?.RO?.find((r) => r.roName.toUpperCase() === decodedRegion.toUpperCase()) ?? data?.RO?.[0];
    if (!ro) return null;
    return { stats: ro, schools: data?.Schools?.length ?? null };
  }, [selectedGroup, canQuery, catSubs, selectedSegment, catData0, catData1, catData2, catData3, catData4, decodedRegion]);

  const segmentLabel = catSubs.find((s) => s.value === selectedSegment)?.label ?? '';

  // ── Early returns ─────────────────────────────────────────────────────────
  if (!decodedRegion) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-500">Invalid region name.</p>
        <button onClick={() => navigate('..')} className="text-[#F36B21] text-sm mt-2 hover:underline">Go back</button>
      </div>
    );
  }
  if (isRoSummaryLoading && !regionSummary) {
    return (
      <div className="p-6 max-w-[1600px] mx-auto">
        <div className="bg-white rounded-xl border border-gray-200 p-6 text-sm text-gray-500">Loading region summary...</div>
      </div>
    );
  }
  if (!regionSummary || isRoSummaryError) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-500">Region "{decodedRegion}" data not found.</p>
        <button onClick={() => navigate('..')} className="text-[#F36B21] text-sm mt-2 hover:underline">Go back</button>
      </div>
    );
  }

  // Cards follow the classification: region totals on 'Overall', otherwise the
  // selected segment's figures.
  const stats = activeSegment?.stats ?? regionSummary;
  const statsScope = activeSegment ? segmentLabel : `Class ${grade}`;
  // The per-segment endpoint returns no school list, and the school count is a
  // property of the region rather than of a gender/category/stream split, so
  // fall back to the region's own count instead of blanking the card.
  const schoolCount = activeSegment?.schools ?? regionSchools.length;

  const cards = [
    { title: 'Total Students', value: stats.totalRegistered, subtitle: statsScope, icon: <Users className="w-5 h-5" />, color: 'purple' as const },
    { title: 'Pass %', value: `${stats.passPercentage}%`, subtitle: `${stats.totalPassed} passed`, icon: <CheckCircle2 className="w-5 h-5" />, color: 'green' as const },
    { title: 'Compartment', value: stats.totalCompartment, subtitle: 'Students', icon: <BookOpen className="w-5 h-5" />, color: 'orange' as const },
    { title: 'Perf. Index', value: stats.performanceIndex, subtitle: 'Performance Index', icon: <Award className="w-5 h-5" />, color: 'blue' as const },
    { title: 'Ess. Repeat', value: stats.totalEssentialRepeat, subtitle: 'Students', icon: <XCircle className="w-5 h-5" />, color: 'red' as const },
    { title: 'Schools', value: schoolCount, subtitle: `${stats.totalAbsent} absent`, icon: <TrendingUp className="w-5 h-5" />, color: 'teal' as const },
  ];

  const isOverall = selectedGroup === 'overall';
  const activeRows = isOverall ? schoolEntityRows : comparisonRows;
  // Streams exist only for class 12, so this chart is rendered for grade 12 alone.
  const subjectChartTitle = 'Stream-wise Performance (Region)';

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-5">
      {isRoSummaryLoading && (
        <div className="text-sm text-gray-500 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
          Refreshing region data...
        </div>
      )}

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('..')} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500">
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold text-gray-900">{regionSummary.region || decodedRegion} Region</h1>
          <p className="text-sm text-gray-500">
            Class {grade} — {selectedYear}
            {activeSegment ? ` — ${segmentLabel}` : ''} — {(stats.totalRegistered ?? 0).toLocaleString()} students
            {` — ${schoolCount} schools`}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center flex-shrink-0">
          <div className="bg-white rounded-xl border border-gray-200 inline-flex overflow-hidden">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`flex items-center gap-2 px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.id ? 'text-[#F36B21] border-[#F36B21] bg-orange-50' : 'text-gray-600 border-transparent hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
          <ResultYearDropdown grade={grade} value={selectedYear} onChange={handleYearChange} />
        </div>
      </div>

      {/* ── KPI Cards ────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {cards.map((c) => (
          <StatCard key={c.title} title={c.title} value={c.value} subtitle={c.subtitle} icon={c.icon} color={c.color} />
        ))}
      </div>

      {/* ── Filter panel ─────────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm divide-y divide-gray-100">

        {/* View */}
        <div className="px-6 py-3 flex flex-wrap items-center gap-3">
          <span className="text-sm font-bold text-gray-400 uppercase tracking-wide w-36 flex-shrink-0 whitespace-nowrap">View</span>
          <ViewModeToggle value={viewMode} onChange={setViewMode} />
        </div>

        {/* Classification */}
        <div className="px-6 py-3 flex flex-wrap items-center gap-3">
          <span className="text-sm font-bold text-gray-400 uppercase tracking-wide w-36 flex-shrink-0 whitespace-nowrap">Classification</span>
          <div className="flex flex-wrap gap-2">
            {allGroups.map((g) => (
              <Chip key={g.id} label={g.label} active={selectedGroup === g.id} onClick={() => handleGroupChange(g.id)} />
            ))}
          </div>
        </div>

        {/* Segment sub-row */}
        {activeGroupDef.segments.length > 0 && (
          <div className="px-6 py-2.5 flex flex-wrap items-center gap-3 bg-gray-50/60">
            <span className="text-sm font-bold text-gray-400 uppercase tracking-wide w-36 flex-shrink-0 whitespace-nowrap">Segment</span>
            <div className="flex flex-wrap gap-2">
              {activeGroupDef.segments.map((seg) => (
                <Chip key={seg.value} label={seg.label} active={selectedSegment === seg.value} onClick={() => setSelectedSegment(seg.value)} />
              ))}
            </div>
          </div>
        )}

        {/* Metric */}
        <div className="px-6 py-3 flex flex-wrap items-center gap-3">
          <span className="text-sm font-bold text-gray-400 uppercase tracking-wide w-36 flex-shrink-0 whitespace-nowrap">Metric</span>
          <div className="flex bg-gray-100 rounded-lg p-0.5">
            {METRIC_MODES.map((m) => (
              <button
                key={m.id}
                onClick={() => setMetricMode(m.id)}
                className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-colors ${
                  metricMode === m.id ? 'bg-white text-[#F36B21] shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Active filter badge ───────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-2 text-sm text-gray-500">
        <span className="font-semibold text-gray-700">Showing:</span>
        <span className="px-2 py-0.5 bg-orange-50 border border-orange-200 rounded-full text-orange-700 font-semibold">
          Class {grade}
        </span>
        <span className="px-2 py-0.5 bg-blue-50 border border-blue-100 rounded-full text-blue-700 font-semibold">
          {selectedYear}
        </span>
        <span className="px-2 py-0.5 bg-green-50 border border-green-100 rounded-full text-green-700 font-semibold">
          {displaySegmentLabel}
        </span>
        <span className="px-2 py-0.5 bg-gray-100 border border-gray-200 rounded-full text-gray-700 font-semibold">
          {METRIC_MODES.find((m) => m.id === metricMode)?.label}
        </span>
      </div>

      {/* ── Overall: Subject chart + School-wise analysis ─────────────────── */}
      {isOverall && (
        <>
          {grade === 12 && (
            <SubjectPerformanceChart data={regionSubjects} title={subjectChartTitle} />
          )}

          {viewMode === 'graphical' && (
            <div>
              <SectionLabel>School Performance — {decodedRegion} Region</SectionLabel>
              <EntityBarChart
                data={schoolEntityRows}
                metric={metric}
                title={`Schools — ${regionSummary.region || decodedRegion} Region`}
                limit={30}
                onBarClick={(name) => {
                  const school = schoolEntityRows.find((r) => r.name === name);
                  if (school?.kvCode) navigate('school/' + school.kvCode + '?class=' + activeTab + '&year=' + selectedYear);
                }}
              />
            </div>
          )}

          {viewMode === 'tabular' && (
            <div>
              <SectionLabel>School-wise Detail</SectionLabel>
              <SchoolWiseTable data={regionSchools} activeTab={activeTab} year={selectedYear} />
            </div>
          )}
        </>
      )}

      {/* ── Category classification ───────────────────────────────────────── */}
      {!isOverall && (
        <>
          {viewMode === 'graphical' && (
            <div>
              <SectionLabel>{displaySegmentLabel} — Breakdown</SectionLabel>
              {activeRows.length > 0 ? (
                <EntityBarChart
                  data={activeRows}
                  metric={metric}
                  title={`${displaySegmentLabel} — ${regionSummary.region || decodedRegion}`}
                  onBarClick={(name) => {
                    const school = activeRows.find((r) => r.name === name);
                    if (school?.kvCode) navigate('school/' + school.kvCode + '?class=' + activeTab + '&year=' + selectedYear);
                  }}
                />
              ) : (
                <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-gray-400 text-sm">
                  No data available for this classification.
                </div>
              )}
            </div>
          )}

          {viewMode === 'tabular' && (
            <div>
              <SectionLabel>{displaySegmentLabel} — Tabular Breakdown</SectionLabel>
              <div className="bg-white rounded-xl border border-gray-200 overflow-auto">
                <table className="w-full text-sm text-left">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="py-3 px-4 font-semibold text-gray-600">{activeGroupDef.label}</th>
                      <th className="py-3 px-4 font-semibold text-gray-600">Appeared</th>
                      <th className="py-3 px-4 font-semibold text-gray-600">Passed</th>
                      <th className="py-3 px-4 font-semibold text-gray-600">Compartment</th>
                      <th className="py-3 px-4 font-semibold text-gray-600">Pass %</th>
                      <th className="py-3 px-4 font-semibold text-gray-600">Perf. Index</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {activeRows.length > 0 ? activeRows.map((row) => (
                      <tr key={row.name} className="hover:bg-orange-50 transition-colors">
                        <td className="py-3 px-4 font-medium text-gray-800">{row.name}</td>
                        <td className="py-3 px-4 text-gray-700">{row.totalAppeared.toLocaleString()}</td>
                        <td className="py-3 px-4 text-green-700 font-medium">{row.totalPassed.toLocaleString()}</td>
                        <td className="py-3 px-4 text-orange-600">{row.totalCompartment}</td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-0.5 rounded-full text-sm font-semibold ${
                            row.passPercentage >= regionSummary.passPercentage ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                          }`}>
                            {row.passPercentage}%
                          </span>
                        </td>
                        <td className={`py-3 px-4 font-medium ${
                          row.performanceIndex >= regionSummary.performanceIndex ? 'text-green-700' : 'text-red-600'
                        }`}>
                          {row.performanceIndex}
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={6} className="py-8 text-center text-gray-400">No data available.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
